import { IAnalyticsServiceConfiguration } from '../../../../../services/analytics'
import { AnalyticsMetaKeys as key } from '../../../../../services/analytics/formatters/meta-keys'
import { Utils } from '../../../../../utils/common'
import { DateFormat, DateUtils } from '../../../../../utils/date'
import { DOMUtils } from '../../../../../utils/dom'
import { KlangooUtils } from '../../../../../utils/klangoo'
import { ScreenUtils } from '../../../../../utils/screen'
import { IMain } from '../../../../conf/page'

const NLP_ENTITY_SCORE_THRESHOLD = .25
const NLP_ENTITY_RANK = ['R', 'VR']
const NULL_SUB_SEC = 'NULL'
const DEFAULT_PAGE_STATUS = 200
const DATE_FORMAT = DateFormat.YYYY_MM_DD
const TIME_FORMAT = DateFormat.HH_MM_SS
const UTM_SRC = 'utm_source'

export interface IGlobalDataFormatter {
    lookup(attributeName: string): string
}

export class GlobalDataFormatter implements IGlobalDataFormatter {

    public constructor(private conf: IAnalyticsServiceConfiguration) {
        return
    }

    /**
     * get the value of a global attribute given its attribute name
     * @param: string
     * @returns: string
     */

    public lookup(attributeName: string): string {
        let attributeValue = ''
        switch (attributeName) {
            case key.USER_LOGGEDIN:
                attributeValue = String(!!this.conf.profile.email)
                break
            case key.AD_SITE:
                attributeValue = this.conf.adSite
                break
            case key.AD_LIGHT:
                attributeValue = String(this.conf.profile.adLight)
                break
            case key.BRAND:
                attributeValue = this.conf.brand
                break
            case key.DIVISION:
                attributeValue = this.conf.division
                break
            case key.DOMAIN:
                attributeValue = this.conf.domain
                break
            case key.VENDOR:
                attributeValue = this.conf.vendor
                break
            case key.DEBUG:
                attributeValue = this.conf.debug
                break
            case key.TYPE:
                attributeValue = this.conf.page.type
                break
            case key.PAYWALL:
                attributeValue = String(this.conf.page.isPaywallWhitelisted)
                break
            case key.METERED_CONTENT_TYPE:
                attributeValue = this.expandPaywallText(this.conf.page.paywall)
                break
            case key.ARTICLE_ID:
                attributeValue = this.conf.page.articleId
                break
            case key.IS_ORIGINAL:
                attributeValue = String(this.conf.page.isOrigin)
                break
            case key.ORIGINAL_ID:
                attributeValue = this.conf.page.originId
                break
            case key.GLOBAL_ID:
                attributeValue = `${this.conf.page.articleId}, ${this.conf.page.originId}`
                break
            case key.DISTRIBUTOR:
                attributeValue = this.conf.page.dist
                break
            case key.PAGE_RES_TYPE:
                if (this.conf.page.error) {
                    attributeValue = String(this.conf.page.error)
                } else {
                    attributeValue = String(DEFAULT_PAGE_STATUS)
                }
                break
            case key.ERROR:
                if (this.conf.page.error) {
                    attributeValue = String(this.conf.page.error).match(/20[01]/) ? String(false) : String(true)
                } else {
                    attributeValue = String(false)
                }
                break
            case key.EXCERPT:
                attributeValue = this.conf.page.excerpt
                break
            case key.VID_IN_PAGE:
                if (this.conf.page.video) {
                    attributeValue = String(this.conf.page.video.inPage)
                }
                break
            case key.WRD_CNT:
                attributeValue = String(this.conf.page.wc)
                break
            case key.SPONSOR:
                attributeValue = this.conf.page.sponsor
                break
            case key.WIRE_ID:
                attributeValue = this.conf.page.wireId
                break
            case key.IS_WIRE:
                attributeValue = (this.conf.page.writers &&
                    this.conf.page.writers.wire &&
                    this.conf.page.writers.wire.length > 0) ||
                    this.conf.page.wireId ? String(true): String(false)
                break
            case key.WRITERS:
                if (this.conf.page.writers && this.conf.page.writers.main) {
                    attributeValue = this.conf.page.writers.main.map((_: IMain) =>
                        typeof _ === 'object' ? _.name : _).join('|')
                }
                break
            case key.WIRE_AUTHORS:
                if (this.conf.page.writers && this.conf.page.writers.wire) {
                    attributeValue = this.conf.page.writers.wire.join('|')
                }
                break
            case key.BYLINE:
                if (this.conf.page.writers && this.conf.page.writers.byline) {
                    attributeValue = this.conf.page.writers.byline
                }
                break
            case key.NEWS_KEYWRD:
                if (this.conf.page.kw) {
                    attributeValue = this.conf.page.kw.join(', ')
                }
                break
            case key.META_TAG:
                if (this.conf.page.tags) {
                    attributeValue = this.conf.page.tags.join(', ')
                }
                break
            case key.SLUG:
                attributeValue = String(this.conf.page.globalSlug)
                break
            case key.TITLE:
                attributeValue = this.conf.page.getTitle()
                break
            case key.HEADLINE:
                attributeValue = this.conf.page.getHeadline()
                break
            case key.MAIN_CATEGORY:
                const categories = this.conf.page.getCategories()
                if (categories.length) {
                    attributeValue = categories[categories.length - 1]
                }
                break
            case key.CATEGORY:
                attributeValue = this.conf.page.getCategories()
                    .concat([NULL_SUB_SEC]).shift()
                break
            case key.ALL_SUB_SEC:
                attributeValue = this.conf.page.getCategories().join('/')
                break
            case key.SUB_SEC:
                const categoriesPadded2 = this.conf.page.getCategories()
                    .concat([NULL_SUB_SEC, NULL_SUB_SEC])
                categoriesPadded2.shift()
                attributeValue = categoriesPadded2.shift()
                break
            case key.SUB_SEC_2:
                const categoriesPadded3 = this.conf.page.getCategories()
                    .concat([NULL_SUB_SEC, NULL_SUB_SEC, NULL_SUB_SEC])
                categoriesPadded3.shift()
                categoriesPadded3.shift()
                attributeValue = categoriesPadded3.shift()
                break
            case key.SUB_SEC_3:
                const categoriesPadded4 = this.conf.page.getCategories()
                    .concat([NULL_SUB_SEC, NULL_SUB_SEC, NULL_SUB_SEC, NULL_SUB_SEC])
                categoriesPadded4.shift()
                categoriesPadded4.shift()
                categoriesPadded4.shift()
                attributeValue = categoriesPadded4.shift()
                break
            case key.PUB_DATE:
                if (this.conf.page.date && this.conf.page.date.pub) {
                    attributeValue = DateUtils.format(this.conf.page.date.pub, DATE_FORMAT)
                }
                break
            case key.PUB_TIME:
                if (this.conf.page.date && this.conf.page.date.pub) {
                    attributeValue = DateUtils.format(this.conf.page.date.pub, TIME_FORMAT)
                }
                break
            case key.MOD_DATE:
                if (this.conf.page.date && this.conf.page.date.mod) {
                    attributeValue = DateUtils.format(this.conf.page.date.mod, DATE_FORMAT)
                }
                break
            case key.MOD_TIME:
                if (this.conf.page.date && this.conf.page.date.mod) {
                    attributeValue = DateUtils.format(this.conf.page.date.mod, TIME_FORMAT)
                }
                break
            case key.KLNGO_ENTITY:
                if(this.conf.page.klangoo && this.conf.page.klangoo.entities) {
                    attributeValue = [...new Set(
                        [...KlangooUtils.stringifyFilteredEntities(this.conf.page.klangoo.entities,
                            NLP_ENTITY_RANK, NLP_ENTITY_SCORE_THRESHOLD),
                        ...KlangooUtils.stringifyFilteredKeytopics(this.conf.page.klangoo.keyTopics,
                            NLP_ENTITY_SCORE_THRESHOLD),
                        ...KlangooUtils.stringifyFilteredCategories(this.conf.page.klangoo.categories,
                            NLP_ENTITY_SCORE_THRESHOLD)]
                    )].join('| ')
                }
                break
            case key.NLP_ENTITY:
                if(this.conf.page.klangoo && this.conf.page.klangoo.entities) {
                    attributeValue = KlangooUtils.stringifyFilteredEntities(
                        this.conf.page.klangoo.entities).join('| ')
                }
                break
            case key.NLP_TOPIC:
                if(this.conf.page.klangoo && this.conf.page.klangoo.keyTopics) {
                    attributeValue = KlangooUtils.stringifyFilteredKeytopics(
                        this.conf.page.klangoo.keyTopics).join('| ')
                }
                break
            case key.KLNGO_CATEGORY:
                if(this.conf.page.klangoo && this.conf.page.klangoo.categories) {
                    attributeValue = KlangooUtils.stringifyFilteredCategories(
                        this.conf.page.klangoo.categories, NLP_ENTITY_SCORE_THRESHOLD).join('| ')
                }
                break
            case key.NAME:
                attributeValue = location.href.split(location.host)[1]
                break
            case key.CUR_PAGE:
                attributeValue = Utils.currentPageUrl()
                break
            case key.META_DESC:
                if (document.querySelector(`meta[name='description']`)) {
                    attributeValue = Utils.decodeHtmlEntity(
                        DOMUtils.getMetaContent('description'))
                }
                break
            case key.META_ROBOT:
                if (document.querySelector(`meta[name='robots']`)) {
                    attributeValue = Utils.decodeHtmlEntity(
                        DOMUtils.getMetaContent('robots'))
                }
                break
            case key.CANONICAL_PATH:
                if (document.querySelector("link[rel='canonical']")) {
                    attributeValue = DOMUtils.getRelUrl('canonical')
                }
                break
            case key.REF_DETAILS:
                const params = Utils.urlParams(document.location)
                if (params.has(UTM_SRC)) {
                    attributeValue = params.get(UTM_SRC)
                }
                break
            case key.PAGEVIEW_INIT_PRCNT:
                attributeValue = String(ScreenUtils.initPageYcord())
                break
            case key.PAGEVIEW_PRCNT:
                attributeValue = String(ScreenUtils.crntPageYcord())
                break
            case key.PROJECT_TYPE:
                if(this.conf.page.projectType !== undefined) {
                    attributeValue = String(this.conf.page.projectType)
                }
                break
            case key.PLATFORM:
                attributeValue = this.conf.platform
                break
            case key.PLATFORM_VERSION:
                attributeValue = this.conf.platformVersion
                break
            case key.FEM_VERSION:
                attributeValue = this.conf.femVersion
                break
        }
        return attributeValue
    }

    /**
     * convert short paywall text, comma separated, into comma separated full descriptions
     * @param: string
     * @returns: string
     */

    public expandPaywallText(paywallText: string): string {
        if (paywallText) {
            const termLookup: any = {
                ccate: 'Commercial Category',
                exclusive: 'Exclusive',
                fwpost: 'Full Width Post',
                hflist: 'Hide Post From List',
                nofollow: 'No Follow',
                noindex: 'No Index',
                subscriber: 'Subscriber Only',
                whitelist: 'Paywall Whitelist',
            }
            return paywallText
                .split(', ')
                .map((term: string) => termLookup[term] ? termLookup[term] : 'Metered Content')
                .join(', ')
        }
        return ''
    }
}
